/**
 * Staff PIN policy and session/lockout helpers. Verification itself is server-side only
 * (bcrypt via pgcrypto). These helpers validate input before it is sent and compute UI state.
 */
export const PIN_MIN_LENGTH = 4;
export const PIN_MAX_LENGTH = 6;

const TRIVIAL = new Set(['1234', '4321', '12345', '123456', '654321', '0000', '00000', '000000']);

/** Approvers of money or owner-level actions use this length (ADR-023). */
export const APPROVER_PIN_LENGTH = 6;

export type PinValidation = { ok: true } | { ok: false; error: string };

/**
 * `sixDigits`: the employee can approve money or owner-level actions, so the PIN must have
 * exactly 6 digits (mirrors app.set_employee_pin).
 */
export function validatePin(pin: string, options: { sixDigits?: boolean } = {}): PinValidation {
  if (!/^\d+$/.test(pin)) return { ok: false, error: 'PIN must contain digits only' };
  if (pin.length < PIN_MIN_LENGTH || pin.length > PIN_MAX_LENGTH)
    return { ok: false, error: `PIN must be ${PIN_MIN_LENGTH} to ${PIN_MAX_LENGTH} digits` };
  if (options.sixDigits && pin.length !== APPROVER_PIN_LENGTH)
    return {
      ok: false,
      error: `This employee approves money or owner-level actions: the PIN must have ${APPROVER_PIN_LENGTH} digits`,
    };
  if (TRIVIAL.has(pin) || /^(\d)\1+$/.test(pin))
    return { ok: false, error: 'PIN is too easy to guess' };
  return { ok: true };
}

export interface LockoutPolicy {
  maxAttempts: number;
  lockoutSeconds: number;
}

export interface LockoutState {
  locked: boolean;
  failuresInWindow: number;
  retryAfterSeconds: number;
}

/**
 * Mirror of the server rule: count failed attempts inside the lockout window; when the count
 * reaches `maxAttempts`, the employee is locked (on every terminal, for signing in and for
 * approving) until the oldest counted failure leaves the window.
 */
export function computeLockout(
  failedAttemptTimes: readonly Date[],
  now: Date,
  policy: LockoutPolicy,
): LockoutState {
  const windowStart = now.getTime() - policy.lockoutSeconds * 1000;
  const inWindow = failedAttemptTimes
    .map((d) => d.getTime())
    .filter((t) => t > windowStart)
    .sort((a, b) => a - b);
  if (inWindow.length < policy.maxAttempts) {
    return { locked: false, failuresInWindow: inWindow.length, retryAfterSeconds: 0 };
  }
  const oldestCounted = inWindow[inWindow.length - policy.maxAttempts]!;
  const retryAfterSeconds = Math.max(
    1,
    Math.ceil((oldestCounted + policy.lockoutSeconds * 1000 - now.getTime()) / 1000),
  );
  return { locked: true, failuresInWindow: inWindow.length, retryAfterSeconds };
}

export function isSessionIdle(lastActivityAt: Date, now: Date, idleLockSeconds: number): boolean {
  return now.getTime() - lastActivityAt.getTime() >= idleLockSeconds * 1000;
}
